import './TourLauncher.scss';
import { FC, useEffect, useRef, useState } from 'react';
import { useTypedSelector } from '@hooks/useTypedSelector';
import { useActions } from '@hooks/useActions';
import AnchoredDialog from '@srm/ui/anchored-dialog';
import { MdOutlineSchool, MdSearch } from 'react-icons/md';
import { matchRoutePattern } from './tourUtils';
import { rememberTourOrigin } from './tourOrigin';
import { useCurrentRoute } from './useCurrentRoute';
import { formatLauncherSummary, sortLauncherTours, TOUR_LAUNCHER_SEARCH_THRESHOLD } from './tourLauncherList';

export interface ITourLauncherProps {
    isMenuOpen: boolean;
    onOpenChange?: (open: boolean) => void;
}

const TourLauncher: FC<ITourLauncherProps> = ({ isMenuOpen, onOpenChange }) => {
    const anchorElement = useRef<HTMLButtonElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    useEffect(() => {
        onOpenChange?.(isOpen);
    }, [isOpen, onOpenChange]);
    useEffect(() => {
        if (!isOpen) setQuery('');
    }, [isOpen]);
    const { tours, activeTourId } = useTypedSelector((state) => state.tours);
    const currentFlowName = useTypedSelector((state) => state.document?.scheme?.flowName);

    const { startTour, resetTourProgress } = useActions();
    const currentRoute = useCurrentRoute();

    const availableTours = tours.filter(
        (t) =>
            matchRoutePattern(t.routePattern, currentRoute) &&
            (!t.targeting?.flowNames?.length || (currentFlowName && t.targeting.flowNames.includes(currentFlowName))),
    );

    if (availableTours.length === 0) return null;

    const uncompletedCount = availableTours.filter((t) => !t.progress || t.progress.status === 'not_started').length;
    const showSearch = availableTours.length > TOUR_LAUNCHER_SEARCH_THRESHOLD;
    const normalizedQuery = query.trim().toLocaleLowerCase('ru');
    const visibleTours = sortLauncherTours(availableTours).filter((tour) =>
        tour.title.toLocaleLowerCase('ru').includes(normalizedQuery),
    );

    const handleStartTour = (tourId: string) => {
        rememberTourOrigin(tourId, anchorElement.current);
        setIsOpen(false);
        startTour(tourId);
    };

    const handleResetAndStart = (tourId: string) => {
        rememberTourOrigin(tourId, anchorElement.current);
        setIsOpen(false);
        resetTourProgress(tourId);
        setTimeout(() => startTour(tourId), 300);
    };

    return (
        <div className="tour-launcher">
            <AnchoredDialog
                align="left"
                target={
                    <button
                        ref={anchorElement}
                        type="button"
                        className="tour-launcher__button"
                        aria-label="Обучающие туры"
                        disabled={!!activeTourId}
                        onClick={() => setIsOpen((prev) => !prev)}
                    >
                        <span className="tour-launcher__icon">
                            <MdOutlineSchool size="24" />
                            {uncompletedCount > 0 && <span className="tour-launcher__badge">{uncompletedCount}</span>}
                        </span>
                        {isMenuOpen && <span className="tour-launcher__label">Обучающие туры</span>}
                    </button>
                }
                className="rf-navbar-settings-dialog tour-launcher__dialog"
                contentClassName="rf-navbar-settings-dialog__content tour-launcher__content"
                opened={isOpen}
                onClose={() => setIsOpen(false)}
                width="auto"
                widthMode="content"
                minWidth={320}
                placement="right"
                sideAlign="end"
                verticalOffset={10}
            >
                <header className="tour-launcher__head">
                    <h2 className="tour-launcher__title">Обучающие туры</h2>
                    <p className="tour-launcher__summary">{formatLauncherSummary(availableTours)}</p>
                </header>
                {showSearch && (
                    <label className="tour-launcher__search">
                        <MdSearch size={18} aria-hidden />
                        <input
                            type="search"
                            value={query}
                            placeholder="Найти тур"
                            aria-label="Найти тур"
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape' && query) {
                                    event.stopPropagation();
                                    setQuery('');
                                }
                            }}
                        />
                    </label>
                )}
                <div className="tour-launcher__list">
                    {visibleTours.length === 0 ? (
                        <p className="tour-launcher__empty">Нет тура с таким названием</p>
                    ) : (
                        visibleTours.map((tour) => {
                            const isCompleted = tour.progress?.status === 'completed';
                            const isSkipped = tour.progress?.status === 'skipped';
                            const isInProgress = tour.progress?.status === 'in_progress';
                            return (
                                <div key={tour.id} className="rf-navbar-settings-dialog__item">
                                    <div
                                        className="tour-launcher__menu-item"
                                        onClick={() =>
                                            isCompleted || isSkipped
                                                ? handleResetAndStart(tour.id)
                                                : handleStartTour(tour.id)
                                        }
                                    >
                                        <span className="tour-launcher__menu-item-title">{tour.title}</span>
                                        <span
                                            className="tour-launcher__menu-item-status"
                                            data-status={isCompleted ? 'completed' : isSkipped ? 'skipped' : 'new'}
                                        >
                                            {isCompleted ? 'Пройден' : isSkipped ? 'Пропущен' : isInProgress ? 'В процессе' : 'Не начат'}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </AnchoredDialog>
        </div>
    );
};

export default TourLauncher;
